<script setup lang="ts">
import { Download, Loader2, Users } from 'lucide-vue-next';
import { computed, ref } from 'vue';

import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/toast/use-toast';
import {
    type AzureGroup,
    createUserGroup,
    type UserGroup
} from '@/services/user_groups';

interface Props {
    group: AzureGroup;
    tenantId: string;
    userGroups: UserGroup[];
}

const props = defineProps<Props>();
const emit = defineEmits<{
    'group-imported': [];
    'view-members': [];
}>();

const { toast } = useToast();

const importing = ref(false);

const isAlreadyImported = computed(() => {
    return props.userGroups.some(
        (userGroup) => userGroup.azure_group_id === props.group.id
    );
});

const handleImport = async (event: Event) => {
    event.stopPropagation();

    importing.value = true;

    try {
        await createUserGroup({
            name: props.group.displayName,
            description: props.group.description || undefined,
            azure_group_id: props.group.id,
            tenant_id: props.tenantId
        });

        toast({
            title: 'Group Imported',
            description: 'The group has been successfully created',
            duration: 3000
        });

        emit('group-imported');
    } catch (err) {
        console.error('Error importing group:', err);

        const errorMessage =
            err instanceof Error ? err.message : 'Failed to import group';

        toast({
            title: 'Import Failed',
            description: errorMessage,
            variant: 'destructive',
            duration: 5000
        });
    } finally {
        importing.value = false;
    }
};

const handleViewMembers = (event: Event) => {
    event.stopPropagation();
    emit('view-members');
};
</script>

<template>
    <div
        class="bg-card border rounded-lg shadow-sm hover:shadow-md transition-shadow overflow-hidden"
    >
        <!-- Group Header -->
        <div class="p-6">
            <div class="flex items-center gap-3">
                <!-- Group info -->
                <div class="flex-1">
                    <h3 class="font-bold text-xl mb-2">
                        {{ group.displayName }}
                    </h3>
                    <p
                        v-if="group.description"
                        class="text-sm text-muted-foreground mb-3"
                    >
                        {{ group.description }}
                    </p>
                </div>

                <!-- Action buttons -->
                <div class="flex items-center gap-2 flex-shrink-0 ml-4">
                    <Button
                        items-center
                        gap-3
                        @click="handleImport"
                        :disabled="importing || isAlreadyImported"
                        size="sm"
                        variant="default"
                    >
                        <Loader2
                            v-if="importing"
                            class="w-4 h-4 mr-2 animate-spin"
                        />
                        <Download
                            v-else-if="!isAlreadyImported"
                            class="w-4 h-4 mr-2"
                        />
                        {{
                            isAlreadyImported
                                ? 'Imported'
                                : importing
                                  ? 'Importing...'
                                  : 'Import'
                        }}
                    </Button>
                    <Button
                        variant="secondary"
                        size="sm"
                        class="flex items-center gap-2"
                        @click="handleViewMembers"
                        title="View Members"
                    >
                        <Users class="w-4 h-4" />
                        <p class="font-normal">View Members</p>
                    </Button>
                </div>
            </div>
        </div>
    </div>
</template>
